import { Menu, MenuButton } from '@chakra-ui/menu';
import { Center, Icon, IconButton, Portal } from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import mixpanel from 'mixpanel-browser';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import {
  FeatureFlag,
  Loading,
  PageTitles,
  UserTrackingEvents,
  WILSON_CLIENT_NAMES,
} from '@revelio/core';

import { useBrand } from '../../shared/branding/brand-provider';
import { showAlertDialog } from '../../shared/components/alert-dialog';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { useEffectiveUserId } from '../adminRewrite/impersonation/useEffectiveUserId';
import DashboardPage from '../DashboardPage';
import { MiddlewareReportMenuList } from './components/mw-report-menu-list';
import { ReportIframe } from './components/report-iframe';
import { useAllReports } from './hooks/use-all-reports';
import styles from './report-page.module.css';

const ReportPage = () => {
  useLifecycles(
    () => {
      mixpanel.track(UserTrackingEvents.PAGE_VISIT, {
        page: PageTitles.REPORTS,
      });
      mixpanel.time_event(UserTrackingEvents.PAGE_LEAVE);
    },
    () => {
      mixpanel.track(UserTrackingEvents.PAGE_LEAVE, {
        page: PageTitles.REPORTS,
      });
    }
  );

  const params = useParams();
  const [queryParams] = useSearchParams();
  const brand = useBrand();

  const effectiveUserId = useEffectiveUserId();
  const reportUserId = queryParams.get('userId') || effectiveUserId || '';
  const { reports, fetching } = useAllReports({
    userId: reportUserId,
  });
  // @ts-expect-error noUncheckedIndexedAccess
  const report = reports.find((r) => r.id === params.id);

  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const reportsLabel = isNewDashboardLayout
    ? PageTitles.REPORTS_STATUS
    : PageTitles.REPORTS;

  if (fetching || !report || !report.link) {
    return (
      <Center h="100%" w="100%" className={styles.message}>
        {!fetching ? (
          `Sorry, we couldn't find the requested report.`
        ) : (
          <Loading />
        )}
      </Center>
    );
  }

  return (
    <DashboardPage
      title={[reportsLabel, report?.name || '']}
      loading={false}
      topRight={
        report &&
        report.reportType !== 'GeneratedByRevelio' && (
          <Menu placement="left-start">
            <MenuButton
              as={IconButton}
              className={styles.actionsButton}
              icon={
                <Icon
                  as={IconDotGrid1x3Vertical}
                  data-testid="reports_status_menu_button"
                />
              }
              size="xs"
              colorScheme="gray"
            >
              Actions
            </MenuButton>
            <Portal>
              <MiddlewareReportMenuList
                report={report}
                onActionTriggered={(action) => {
                  if (action === 'refresh') {
                    showAlertDialog({
                      alertType: 'actionless',
                      isOpen: true,
                      headerText: 'Report Refreshing',
                      bodyContent: (
                        <span>
                          This report is being regenerated and will take a few
                          minutes. You may leave this page.
                        </span>
                      ),
                    });
                  } else if (action === 'delete') {
                    showAlertDialog({
                      alertType: 'actionless',
                      isOpen: true,
                      headerText: 'Report Deleted Successfully',
                      bodyContent: (
                        <span>
                          This report has been deleted. You may now leave the
                          page.
                        </span>
                      ),
                    });
                  }
                }}
              />
            </Portal>
          </Menu>
        )
      }
    >
      <ReportIframe
        link={
          WILSON_CLIENT_NAMES.has(brand.clientName) &&
          !/[?&]cover=wilson(?:&|$)/.test(report.link)
            ? `${report.link}${report.link.includes('?') ? '&' : '?'}cover=wilson`
            : report.link
        }
      />
    </DashboardPage>
  );
};

export default ReportPage;
